/**
 * The one place a region's consent posture is written down.
 *
 * Everything else derives from `CONSENT_REGIONS` and `GLOBAL_DEFAULT`: the
 * country list the region gate matches against, the inline Google Consent Mode
 * `default` script emitted in `<head>` before any tag loads, and the decision
 * applied for a visitor who has made no choice yet. A region is never
 * enumerated twice — the divergence between a hand-written array and a
 * hand-written `region:[…]` string is exactly what this module removes.
 *
 * The values here (which regions, and each region's per-category default) are a
 * placeholder pending product/legal sign-off — see the OpenSpec change
 * `add-consent-preferences`. The shape is not: a region may require an explicit
 * choice, and it may default the optional categories differently from one
 * another.
 */

export type OptionalCategory = "analytics" | "marketing";
export type ConsentDecision = "granted" | "denied";
export type RegionDefaults = Record<OptionalCategory, ConsentDecision>;

export interface RegionRule {
  /** Whether a visitor here must make an explicit choice before connecting. */
  requiresChoice: boolean;
  /** The decision applied for each optional category until the visitor chooses. */
  defaults: RegionDefaults;
}

/** Applied to a visitor whose region is known and not listed below. */
export const GLOBAL_DEFAULT: RegionRule = {
  requiresChoice: false,
  defaults: { analytics: "granted", marketing: "granted" },
};

/**
 * Applied when the region cannot be determined, or resolves to a placeholder
 * (`XX`) or Tor (`T1`): treat it as needing a choice, with everything optional
 * off until one is made.
 */
export const FAIL_CLOSED: RegionRule = {
  requiresChoice: true,
  defaults: { analytics: "denied", marketing: "denied" },
};

/** Key: ISO 3166-1 alpha-2, or alpha-2 + "-" + subdivision (e.g. `CA-QC`). */
export const CONSENT_REGIONS: Record<string, RegionRule> = Object.fromEntries(
  [
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
    "IS",
    "LI",
    "NO",
    "GB",
    "CH",
    "CA",
  ].map((code) => [
    code,
    {
      requiresChoice: true,
      defaults: { analytics: "denied", marketing: "denied" },
    },
  ]),
);

/** The country codes whose visitors must make an explicit choice. */
export function consentRequiredCountries(): string[] {
  return Object.entries(CONSENT_REGIONS)
    .filter(([, rule]) => rule.requiresChoice)
    .map(([code]) => code)
    .sort();
}

/**
 * The rule for a region code: exact key, then the country prefix, then the
 * global default. A missing, unknown-sentinel or `null` code fails closed.
 */
export function regionRule(code: string | null | undefined): RegionRule {
  if (!code) return FAIL_CLOSED;
  const upper = code.toUpperCase();
  if (upper === "XX" || upper === "T1") return FAIL_CLOSED;
  return (
    CONSENT_REGIONS[upper] ??
    CONSENT_REGIONS[upper.split("-")[0]] ??
    GLOBAL_DEFAULT
  );
}

function consentModeSignals(defaults: RegionDefaults) {
  return {
    ad_storage: defaults.marketing,
    ad_user_data: defaults.marketing,
    ad_personalization: defaults.marketing,
    analytics_storage: defaults.analytics,
  };
}

function objectLiteral(
  entries: Record<string, string | number | readonly string[]>,
): string {
  const parts = Object.entries(entries).map(([key, value]) => {
    if (Array.isArray(value))
      return `${key}:[${value.map((item) => `'${item}'`).join(",")}]`;
    if (typeof value === "number") return `${key}:${value}`;
    return `${key}:'${value}'`;
  });
  return `{${parts.join(",")}}`;
}

/**
 * The exact text of the inline `<head>` script: the `dataLayer`/`gtag`
 * bootstrap, the global `default`, then one `default` per distinct signal tuple
 * among the configured regions, each carrying its own `region:[…]`. Google
 * resolves the region itself, so this is compliance-complete without any geo
 * lookup — the lookup elsewhere only decides *when to ask*.
 */
export function consentModeDefaultScript(): string {
  const bootstrap =
    "window.dataLayer = window.dataLayer || []; " +
    "window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };";
  const globalCall = `gtag('consent','default',${objectLiteral(
    consentModeSignals(GLOBAL_DEFAULT.defaults),
  )});`;

  const groups = new Map<
    string,
    { signals: ReturnType<typeof consentModeSignals>; codes: string[] }
  >();
  for (const [code, rule] of Object.entries(CONSENT_REGIONS)) {
    const signals = consentModeSignals(rule.defaults);
    const key = JSON.stringify(signals);
    const bucket = groups.get(key);
    if (bucket) bucket.codes.push(code);
    else groups.set(key, { signals, codes: [code] });
  }

  const regionCalls = [...groups.values()].map(({ signals, codes }) => {
    return `gtag('consent','default',${objectLiteral({
      ...signals,
      wait_for_update: 500,
      region: [...codes].sort(),
    })});`;
  });

  return [bootstrap, globalCall, ...regionCalls].join(" ");
}

/** How long the country lookup may take before the region fails closed. */
const REGION_LOOKUP_TIMEOUT_MS = 2000;

let inFlight: Promise<RegionRule> | null = null;

/**
 * The visitor's region rule, from Cloudflare's same-origin trace endpoint.
 * Fails closed on every doubt. The result of the real lookup is memoised for
 * the page so the entry screen and the consent surface share one request; a
 * caller that injects its own `fetch` (the tests) is never cached.
 */
export async function fetchRegionRule(
  fetchImpl: typeof fetch = fetch,
): Promise<RegionRule> {
  const shared = fetchImpl === globalThis.fetch;
  if (shared && inFlight) return inFlight;

  const run = (async (): Promise<RegionRule> => {
    try {
      const response = await fetchImpl("/cdn-cgi/trace", {
        signal: AbortSignal.timeout(REGION_LOOKUP_TIMEOUT_MS),
      });
      if (!response.ok) return FAIL_CLOSED;
      const country = /^loc=([A-Z0-9]{2})$/m.exec(await response.text())?.[1];
      return regionRule(country ?? null);
    } catch {
      return FAIL_CLOSED;
    }
  })();

  if (shared) inFlight = run;
  return run;
}

/** Drops the memoised region lookup. Tests only. */
export function resetRegionRuleCache(): void {
  inFlight = null;
}

/** Whether the visitor's region requires an explicit choice. */
export async function isConsentRequiredRegion(
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return (await fetchRegionRule(fetchImpl)).requiresChoice;
}
